import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createBaseAudioContext, setupMicrophoneStream } from '../../../utils/audioUtils';

interface AudioVisualizerProps {
  isRecording: boolean;
  mode: 'listening' | 'speaking' | 'idle' | 'waiting';
  audioUrl?: string;
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ isRecording, mode, audioUrl }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameId = useRef<number>();
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const drawCircularWave = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
      frequencyData: Uint8Array
    ) => {
      ctx.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) / 3;
      
      // Draw base circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(200, 220, 255, 0.2)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw frequency wave
      ctx.beginPath();
      ctx.lineWidth = 2;
      
      const sliceCount = 32; // Number of frequency slices to show
      const step = Math.floor(frequencyData.length / sliceCount);
      
      for (let i = 0; i < sliceCount; i++) {
        // Get average frequency for this slice
        let sum = 0;
        for (let j = 0; j < step; j++) {
          sum += frequencyData[i * step + j];
        }
        const amplitude = (sum / step) / 255;

        const theta = (i / sliceCount) * 2 * Math.PI;
        const offset = radius * (0.8 + amplitude * 0.4);
        
        const x = centerX + Math.cos(theta) * offset;
        const y = centerY + Math.sin(theta) * offset;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.closePath();

      // Set colors based on mode
      const colors = mode === 'listening' 
        ? ['rgba(64, 96, 255, 0.8)', 'rgba(100, 100, 255, 0.8)']  // Blue for user
        : mode === 'speaking'
        ? ['rgba(255, 64, 96, 0.8)', 'rgba(255, 100, 100, 0.8)']  // Red for AI
        : ['rgba(150, 150, 150, 0.8)', 'rgba(170, 170, 170, 0.8)']; // Gray for idle

      const gradient = ctx.createLinearGradient(
        centerX - radius, centerY,
        centerX + radius, centerY
      );
      gradient.addColorStop(0, colors[0]);
      gradient.addColorStop(1, colors[1]);

      ctx.strokeStyle = gradient;
      ctx.stroke();

      // Add glow effect
      ctx.shadowBlur = 15;
      ctx.shadowColor = colors[0];
      ctx.stroke();
      ctx.shadowBlur = 0;
    },
    [mode]
  );

  const drawWaitingAnimation = useCallback(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const time = Date.now() / 1000;
    const rotation = (time * Math.PI) % (2 * Math.PI);
    const size = 40;
    const lineWidth = 2;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    
    // Draw square border
    ctx.strokeStyle = '#808080';
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.rect(-size/2, -size/2, size, size);
    ctx.stroke();
    
    ctx.restore();
    
    animationFrameId.current = requestAnimationFrame(drawWaitingAnimation);
  }, []);

  const animate = useCallback((dataArray?: Uint8Array) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    let frequencyData: Uint8Array;
    
    if (dataArray) {
      frequencyData = dataArray;
    } else if (analyserRef.current) {
      frequencyData = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(frequencyData);
    } else {
      frequencyData = new Uint8Array(128).fill(0);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawCircularWave(ctx, canvas.width, canvas.height, frequencyData);
    animationFrameId.current = requestAnimationFrame(() => animate(dataArray));
  }, [drawCircularWave]);

  const cleanup = useCallback(() => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = undefined;
    }

    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (err) {
        console.warn('Error disconnecting source:', err);
      }
      sourceRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (err) {
        console.warn('Error closing audio context:', err);
      }
    }
    audioContextRef.current = null;

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.src = '';
    }

    delete window.visualizerCallback;
  }, []);

  const setupAudioContext = useCallback(() => {
    const audioContext = createBaseAudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    return { audioContext, analyser };
  }, []);

  const connectMicrophoneToAnalyser = useCallback(async (audioContext: AudioContext, analyser: AnalyserNode) => {
    const source = await setupMicrophoneStream(audioContext);
    if (source) {
      source.connect(analyser);
    }
    sourceRef.current = source;
  }, []);

  useEffect(() => {
    const init = async () => {
      console.log('Initializing with mode:', mode);
      cleanup();
      
      if (mode === 'waiting') {
        console.log('Starting waiting animation');
        drawWaitingAnimation();
      } else if (mode === 'listening') {
        const { audioContext, analyser } = setupAudioContext();
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        
        await connectMicrophoneToAnalyser(audioContext, analyser);
        
        animate();
      } else if (mode === 'speaking') {
        window.visualizerCallback = (dataArray: Uint8Array) => {
          animate(dataArray);
        };
      }
    };

    init().catch(console.error);
    return cleanup;
  }, [mode, cleanup, drawWaitingAnimation, animate, setupAudioContext, connectMicrophoneToAnalyser]);

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 100,
      background: 'rgba(0, 0, 0, 0.05)',
      borderRadius: '8px',
      padding: '10px'
    }}>
      <canvas
        ref={canvasRef}
        width={100}
        height={100}
        style={{
          maxWidth: '100%',
          height: 'auto'
        }}
      />
    </div>
  );
};